import React, { useState, useEffect } from 'react';
import { FiPlus, FiMinus } from 'react-icons/fi';
import useEditorStore from '../../../stores/useEditorStore';
import { ColorPicker, SliderInput } from '../../common';
import { panelStyle, titleStyle, sectionStyle, labelStyle, inputStyle } from '../../../styles/panelStyles';
import {
  createEmptyTable,
  addTableRow, deleteTableRow,
  addTableColumn, deleteTableColumn,
  setTableBorderColor, setTableBorderWidth,
  autoFitRowHeights, recalcTableCells,
} from '../../../utils/tableUtils';
import { mergeCells, unmergeCells, getCellMergeInfo } from '../../../utils/tableMergeUtils';
import { DEFAULT_TEXT_INSET, TABLE_CANVAS_PADDING, TABLE_MIN_NEW_SIZE } from '../../../constants/editorConstants';

const GRID_COLS = 10;
const GRID_ROWS = 8;
const CELL_SIZE = 22;
const CELL_GAP = 2;

// --- 테두리 방향 선택 아이콘 (SVG) ---
// 셀 모드: 단일 셀 도형
const CellBorderIcon = ({ dirs, active }) => {
  const color = active ? '#3b82f6' : '#c0c0c0';
  const bg = '#f3f4f6';
  const w = 2;
  return (
    <svg width="22" height="22" viewBox="0 0 22 22">
      <rect x="3" y="3" width="16" height="16" fill={bg} stroke="none" />
      {dirs.includes('T') && <line x1="3" y1="3" x2="19" y2="3" stroke={color} strokeWidth={w} />}
      {dirs.includes('B') && <line x1="3" y1="19" x2="19" y2="19" stroke={color} strokeWidth={w} />}
      {dirs.includes('L') && <line x1="3" y1="3" x2="3" y2="19" stroke={color} strokeWidth={w} />}
      {dirs.includes('R') && <line x1="19" y1="3" x2="19" y2="19" stroke={color} strokeWidth={w} />}
    </svg>
  );
};

// 테이블 모드: 2x2 그리드 도형
const TableBorderIcon = ({ outside = [], inside = false, active }) => {
  const color = active ? '#3b82f6' : '#c0c0c0';
  const bg = '#f3f4f6';
  const w = 1.8;
  return (
    <svg width="22" height="22" viewBox="0 0 22 22">
      <rect x="2" y="2" width="18" height="18" fill={bg} stroke="none" />
      {/* 바깥 테두리 */}
      {outside.includes('T') && <line x1="2" y1="2" x2="20" y2="2" stroke={color} strokeWidth={w} />}
      {outside.includes('B') && <line x1="2" y1="20" x2="20" y2="20" stroke={color} strokeWidth={w} />}
      {outside.includes('L') && <line x1="2" y1="2" x2="2" y2="20" stroke={color} strokeWidth={w} />}
      {outside.includes('R') && <line x1="20" y1="2" x2="20" y2="20" stroke={color} strokeWidth={w} />}
      {/* 안쪽 십자선 */}
      {inside && <line x1="11" y1="2" x2="11" y2="20" stroke={color} strokeWidth={w} />}
      {inside && <line x1="2" y1="11" x2="20" y2="11" stroke={color} strokeWidth={w} />}
    </svg>
  );
};

const TableSettingsPanel = () => {
  const {
    canvas, canvasSize, selectedObjects, pushHistory, syncLayersFromCanvas,
    selectedTableCells, selectedTableGroup, clearTableCellSelection,
  } = useEditorStore();

  const [hoverCell, setHoverCell] = useState(null);
  const [manualRows, setManualRows] = useState(3);
  const [manualCols, setManualCols] = useState(3);
  // 테두리 방향 선택 (셀 모드: T/B/L/R, 테이블 모드: T/B/L/R + inside)
  const [borderDirs, setBorderDirs] = useState(new Set(['T', 'B', 'L', 'R']));
  // 사용자가 편집 중인 색상/두께 (로컬 상태 — 리셋 방지용)
  const [editColor, setEditColor] = useState(null);
  const [editWidth, setEditWidth] = useState(null);
  // 색상 피커 모달이 열려 있는지 (선택박스/하이라이트 dimming 용)
  const [isBorderColorPickerOpen, setIsBorderColorPickerOpen] = useState(false);

  const selectedTable = selectedObjects.find(obj => obj.type === 'group' && obj._isTableGroup)
    || selectedTableGroup;

  const isCellMode = selectedTableCells.length > 0;
  const isMultiCell = selectedTableCells.length >= 2;
  const singleCell = selectedTableCells.length === 1 ? selectedTableCells[0] : null;
  const cellRect = singleCell && selectedTable
    ? selectedTable.getObjects().find(c => c._isTableCell && c._cellRow === singleCell.row && c._cellCol === singleCell.col)
    : null;
  const cellBgColor = cellRect ? (cellRect.fill || 'transparent') : null;

  const cellMergeInfo = singleCell && selectedTable
    ? getCellMergeInfo(selectedTable, singleCell.row, singleCell.col)
    : { isMerged: false };
  const canMerge = selectedTableCells.length >= 2;
  const canUnmerge = selectedTableCells.length === 1 && cellMergeInfo.isMerged;

  // 테두리 정보: 선택된 방향 기준으로 실제 _borders 값 참조
  const targetRect = cellRect || selectedTable?.getObjects().find(c => c._isTableCell);
  const derivedBorderInfo = React.useMemo(() => {
    if (!targetRect?._borders) {
      return { color: targetRect?._origStroke || '#000000', width: targetRect?._origStrokeWidth || 1 };
    }
    // 선택된 방향 중 첫 번째의 실제 값 사용
    const dirOrder = ['T', 'B', 'L', 'R'];
    const firstDir = dirOrder.find(d => borderDirs.has(d));
    if (firstDir && targetRect._borders[firstDir]) {
      return {
        color: targetRect._borders[firstDir].color || '#000000',
        width: targetRect._borders[firstDir].width ?? 1,
      };
    }
    // inside만 선택된 경우: 내부 셀의 테두리 값 참조
    if (borderDirs.has('inside') && selectedTable) {
      const internalRect = selectedTable.getObjects().find(c =>
        c._isTableCell && (c._cellRow > 0 || c._cellCol > 0) && c._borders
      );
      if (internalRect?._borders) {
        const iDir = internalRect._cellRow > 0 ? 'T' : 'L';
        if (internalRect._borders[iDir]) {
          return { color: internalRect._borders[iDir].color, width: internalRect._borders[iDir].width };
        }
      }
    }
    return { color: targetRect._origStroke || '#000000', width: targetRect._origStrokeWidth || 1 };
  }, [targetRect, borderDirs, selectedTable]);

  // 표시 값: 사용자 편집값 우선, 없으면 캔버스에서 파생
  const currentBorderColor = editColor || derivedBorderInfo.color;
  const currentBorderWidth = editWidth ?? derivedBorderInfo.width;

  // 선택 변경 시 로컬 편집값 + 방향 리셋
  useEffect(() => {
    setEditColor(null);
    setEditWidth(null);
    if (isMultiCell) {
      // 다중셀: 테이블 모드와 동일 (바깥+안쪽)
      setBorderDirs(new Set(['T', 'B', 'L', 'R', 'inside']));
    } else if (isCellMode) {
      setBorderDirs(new Set(['T', 'B', 'L', 'R']));
    } else {
      setBorderDirs(new Set(['T', 'B', 'L', 'R', 'inside']));
    }
  }, [isCellMode, isMultiCell, selectedTableCells.length]);

  // 방향 변경 시 로컬 편집값 리셋 (새 방향의 기존 값을 보여주기 위해)
  useEffect(() => {
    setEditColor(null);
    setEditWidth(null);
  }, [borderDirs]);

  // 색상 피커 모달이 열릴 때만 선택 박스 + 셀 하이라이트를 투명하게
  useEffect(() => {
    if (!selectedTable || !canvas || !isBorderColorPickerOpen) return;
    // 테이블 선택 박스 투명화
    const origBorderColor = selectedTable.borderColor;
    const origCornerColor = selectedTable.cornerColor;
    selectedTable.set({
      borderColor: 'transparent',
      cornerColor: 'transparent',
    });
    // 셀 하이라이트(파란 stroke) 투명화
    const hlCells = selectedTable.getObjects().filter(c => c._isTableCell && c._hlActive);
    const savedHlStrokes = hlCells.map(c => ({ cell: c, stroke: c.stroke, strokeWidth: c.strokeWidth }));
    hlCells.forEach(c => c.set({ stroke: 'transparent', strokeWidth: 0 }));
    canvas.renderAll();
    return () => {
      if (selectedTable.canvas) {
        selectedTable.set({
          borderColor: origBorderColor || '#059669',
          cornerColor: origCornerColor || '#059669',
        });
        // 셀 하이라이트 복원
        savedHlStrokes.forEach(({ cell, stroke, strokeWidth }) => {
          if (cell._hlActive) {
            cell.set({ stroke: stroke || '#3b82f6', strokeWidth: strokeWidth || 2.5 });
          }
        });
        canvas.renderAll();
      }
    };
  }, [selectedTable, canvas, isBorderColorPickerOpen]);

  // --- 테두리 방향 토글 ---
  const toggleBorderDir = (dir) => {
    setBorderDirs(prev => {
      const next = new Set(prev);
      if (isCellMode && !isMultiCell) {
        // 단일셀 모드: 전체 = T+B+L+R
        if (dir === 'all') {
          const allDirs = ['T', 'B', 'L', 'R'];
          if (allDirs.every(d => prev.has(d))) {
            allDirs.forEach(d => next.delete(d));
          } else {
            allDirs.forEach(d => next.add(d));
          }
        } else {
          if (next.has(dir)) next.delete(dir); else next.add(dir);
        }
      } else {
        // 다중셀 / 테이블 모드: 전체 = 바깥+안쪽
        if (dir === 'all') {
          const allDirs = ['T', 'B', 'L', 'R', 'inside'];
          if (allDirs.every(d => prev.has(d))) {
            allDirs.forEach(d => next.delete(d));
          } else {
            allDirs.forEach(d => next.add(d));
          }
        } else if (dir === 'outside') {
          const outerDirs = ['T', 'B', 'L', 'R'];
          if (outerDirs.every(d => prev.has(d))) {
            outerDirs.forEach(d => next.delete(d));
          } else {
            outerDirs.forEach(d => next.add(d));
          }
        } else {
          if (next.has(dir)) next.delete(dir); else next.add(dir);
        }
      }
      return next;
    });
  };

  // --- 테이블 생성 ---
  const handleCreateTable = (rows, cols) => {
    if (!canvas || rows < 1 || cols < 1) return;
    const table = createEmptyTable(rows, cols, canvasSize);
    canvas.add(table);
    canvas.setActiveObject(table);
    canvas.renderAll();
    syncLayersFromCanvas();
    pushHistory();
  };

  // --- 인접 셀의 반대 방향 테두리도 동기화 ---
  // 셀의 R 테두리 = 오른쪽 인접 셀의 L 테두리 (같은 위치에 겹침)
  const ADJACENT_DIR_MAP = { T: 'B', B: 'T', L: 'R', R: 'L' };
  const getAdjacentCell = (table, row, col, dir) => {
    const rows = table._tableRows;
    const cols = table._tableCols;
    // 현재 셀의 span 고려
    const rect = table.getObjects().find(c => c._isTableCell && c._cellRow === row && c._cellCol === col);
    const gs = rect?._gridSpan || 1;
    const rs = rect?._rowSpan || 1;
    if (dir === 'T' && row > 0) return { row: row - 1, col };
    if (dir === 'B' && row + rs - 1 < rows - 1) return { row: row + rs, col };
    if (dir === 'L' && col > 0) return { row, col: col - 1 };
    if (dir === 'R' && col + gs - 1 < cols - 1) return { row, col: col + gs };
    return null;
  };

  const updateBorderLineAndData = (table, row, col, dir, color, width) => {
    table.getObjects().forEach(child => {
      if (child._isTableBorder && child._cellRow === row && child._cellCol === col && child._borderDir === dir) {
        if (width > 0) {
          child.set({ stroke: color, strokeWidth: width, visible: true });
        } else {
          child.set({ visible: false });
        }
      }
      if (child._isTableCell && child._cellRow === row && child._cellCol === col) {
        if (child._borders && child._borders[dir]) {
          child._borders[dir].color = color;
          child._borders[dir].width = width;
        }
      }
    });
  };

  // --- 셀 모드: 선택된 방향만 업데이트 (인접 셀 동기화 포함) ---
  const updateCellBorderDirs = (table, row, col, color, width, dirs) => {
    for (const d of dirs) {
      // 현재 셀의 해당 방향 업데이트
      updateBorderLineAndData(table, row, col, d, color, width);
      // 인접 셀의 반대 방향도 동기화
      const adj = getAdjacentCell(table, row, col, d);
      if (adj) {
        updateBorderLineAndData(table, adj.row, adj.col, ADJACENT_DIR_MAP[d], color, width);
      }
    }
    // _origStroke/_origStrokeWidth 동기화
    if (['T', 'B', 'L', 'R'].every(d => dirs.has(d))) {
      table.getObjects().forEach(child => {
        if (child._isTableCell && child._cellRow === row && child._cellCol === col) {
          child._origStroke = color;
          child._origStrokeWidth = width;
        }
      });
    }
  };

  // --- 테이블 모드: outside/inside 매핑 (인접 셀 동기화 포함) ---
  const applyTableBorderWithDirs = (table, color, width, dirSelection) => {
    const rows = table._tableRows;
    const cols = table._tableCols;
    const rects = table.getObjects().filter(c => c._isTableCell);

    rects.forEach(rect => {
      const r = rect._cellRow;
      const c = rect._cellCol;
      const gs = rect._gridSpan || 1;
      const rs = rect._rowSpan || 1;
      const dirsToUpdate = new Set();

      // 바깥쪽 (T/B/L/R는 테이블 가장자리 셀의 해당 방향)
      if (r === 0 && dirSelection.has('T')) dirsToUpdate.add('T');
      if (r + rs - 1 >= rows - 1 && dirSelection.has('B')) dirsToUpdate.add('B');
      if (c === 0 && dirSelection.has('L')) dirsToUpdate.add('L');
      if (c + gs - 1 >= cols - 1 && dirSelection.has('R')) dirsToUpdate.add('R');

      // 안쪽 (내부 셀 경계)
      if (dirSelection.has('inside')) {
        if (r > 0) dirsToUpdate.add('T');
        if (r + rs - 1 < rows - 1) dirsToUpdate.add('B');
        if (c > 0) dirsToUpdate.add('L');
        if (c + gs - 1 < cols - 1) dirsToUpdate.add('R');
      }

      if (dirsToUpdate.size === 0) return;

      // 각 방향별로 현재 셀 + 인접 셀 동기화
      for (const d of dirsToUpdate) {
        updateBorderLineAndData(table, r, c, d, color, width);
        const adj = getAdjacentCell(table, r, c, d);
        if (adj) {
          updateBorderLineAndData(table, adj.row, adj.col, ADJACENT_DIR_MAP[d], color, width);
        }
      }

      if (['T', 'B', 'L', 'R'].every(d => dirsToUpdate.has(d))) {
        rect._origStroke = color;
        rect._origStrokeWidth = width;
      }
    });
  };

  // --- 다중셀 모드: 선택 영역 기준 outside/inside 매핑 ---
  const applyMultiCellBorderWithDirs = (table, color, width, dirSelection) => {
    const cells = selectedTableCells;
    // 선택 영역의 바운딩 박스 계산 (span 고려)
    let minRow = Infinity, maxRow = -1, minCol = Infinity, maxCol = -1;
    const rects = table.getObjects().filter(c => c._isTableCell);
    for (const cell of cells) {
      const rect = rects.find(r => r._cellRow === cell.row && r._cellCol === cell.col);
      const gs = rect?._gridSpan || 1;
      const rs = rect?._rowSpan || 1;
      minRow = Math.min(minRow, cell.row);
      maxRow = Math.max(maxRow, cell.row + rs - 1);
      minCol = Math.min(minCol, cell.col);
      maxCol = Math.max(maxCol, cell.col + gs - 1);
    }

    for (const cell of cells) {
      const rect = rects.find(r => r._cellRow === cell.row && r._cellCol === cell.col);
      const gs = rect?._gridSpan || 1;
      const rs = rect?._rowSpan || 1;
      const r = cell.row, c = cell.col;
      const dirsToUpdate = new Set();

      // 바깥쪽 (선택 영역의 가장자리)
      if (r === minRow && dirSelection.has('T')) dirsToUpdate.add('T');
      if (r + rs - 1 >= maxRow && dirSelection.has('B')) dirsToUpdate.add('B');
      if (c === minCol && dirSelection.has('L')) dirsToUpdate.add('L');
      if (c + gs - 1 >= maxCol && dirSelection.has('R')) dirsToUpdate.add('R');

      // 안쪽 (선택 영역 내부 경계)
      if (dirSelection.has('inside')) {
        if (r > minRow) dirsToUpdate.add('T');
        if (r + rs - 1 < maxRow) dirsToUpdate.add('B');
        if (c > minCol) dirsToUpdate.add('L');
        if (c + gs - 1 < maxCol) dirsToUpdate.add('R');
      }

      if (dirsToUpdate.size === 0) continue;

      for (const d of dirsToUpdate) {
        updateBorderLineAndData(table, r, c, d, color, width);
        const adj = getAdjacentCell(table, r, c, d);
        if (adj) {
          updateBorderLineAndData(table, adj.row, adj.col, ADJACENT_DIR_MAP[d], color, width);
        }
      }

      if (['T', 'B', 'L', 'R'].every(d => dirsToUpdate.has(d)) && rect) {
        rect._origStroke = color;
        rect._origStrokeWidth = width;
      }
    }
  };

  // --- 테두리 색상/두께 변경 ---
  const handleBorderColorChange = (color) => {
    if (!selectedTable || !canvas) return;
    setEditColor(color);
    if (isMultiCell) {
      applyMultiCellBorderWithDirs(selectedTable, color, currentBorderWidth, borderDirs);
    } else if (isCellMode) {
      selectedTableCells.forEach(cell =>
        updateCellBorderDirs(selectedTable, cell.row, cell.col, color, currentBorderWidth, borderDirs)
      );
    } else {
      applyTableBorderWithDirs(selectedTable, color, currentBorderWidth, borderDirs);
    }
    canvas.renderAll();
    pushHistory();
  };

  const handleBorderWidthChange = (width) => {
    if (!selectedTable || !canvas) return;
    setEditWidth(width);
    if (isMultiCell) {
      applyMultiCellBorderWithDirs(selectedTable, currentBorderColor, width, borderDirs);
    } else if (isCellMode) {
      selectedTableCells.forEach(cell =>
        updateCellBorderDirs(selectedTable, cell.row, cell.col, currentBorderColor, width, borderDirs)
      );
    } else {
      applyTableBorderWithDirs(selectedTable, currentBorderColor, width, borderDirs);
    }
    canvas.renderAll();
    pushHistory();
  };

  // 행/열 추가 후 캔버스를 넘으면 새 행/열 크기를 축소
  const CANVAS_PADDING = TABLE_CANVAS_PADDING;
  const MIN_NEW_SIZE = TABLE_MIN_NEW_SIZE;

  const shrinkNewRowIfNeeded = (table, newRowIdx) => {
    const scY = table.scaleY || 1;
    const bottomEdge = table.top + (table.height * scY) / 2;
    const excess = bottomEdge - (canvasSize.height - CANVAS_PADDING);
    if (excess <= 0) return;
    const cur = table._tableRowHeights[newRowIdx];
    const shrunk = cur - excess / scY;
    if (shrunk < MIN_NEW_SIZE) return;
    table._tableRowHeights[newRowIdx] = shrunk;
    const topEdge = table.top - (table.height * scY) / 2;
    recalcTableCells(table);
    const newH = table._tableRowHeights.reduce((a, b) => a + b, 0);
    table.set({ top: topEdge + (newH * scY) / 2 });
    table.setCoords();
  };

  const shrinkNewColIfNeeded = (table, newColIdx) => {
    const scX = table.scaleX || 1;
    const rightEdge = table.left + (table.width * scX) / 2;
    const excess = rightEdge - (canvasSize.width - CANVAS_PADDING);
    if (excess <= 0) return;
    const cur = table._tableColWidths[newColIdx];
    const shrunk = cur - excess / scX;
    if (shrunk < MIN_NEW_SIZE) return;
    table._tableColWidths[newColIdx] = shrunk;
    const leftEdge = table.left - (table.width * scX) / 2;
    recalcTableCells(table);
    const newW = table._tableColWidths.reduce((a, b) => a + b, 0);
    table.set({ left: leftEdge + (newW * scX) / 2 });
    table.setCoords();
  };

  const handleAddRow = () => {
    if (!selectedTable || !canvas) return;
    const afterRow = singleCell ? singleCell.row : selectedTable._tableRows - 1;
    addTableRow(selectedTable, afterRow);
    shrinkNewRowIfNeeded(selectedTable, afterRow + 1);
    canvas.renderAll();
    pushHistory();
    syncLayersFromCanvas();
  };

  const handleDeleteRow = () => {
    if (!selectedTable || !canvas || selectedTable._tableRows <= 1) return;
    const rowIdx = singleCell ? singleCell.row : selectedTable._tableRows - 1;
    deleteTableRow(selectedTable, rowIdx);
    clearTableCellSelection();
    canvas.renderAll();
    pushHistory();
    syncLayersFromCanvas();
  };

  const handleAddCol = () => {
    if (!selectedTable || !canvas) return;
    const afterCol = singleCell ? singleCell.col : selectedTable._tableCols - 1;
    addTableColumn(selectedTable, afterCol);
    shrinkNewColIfNeeded(selectedTable, afterCol + 1);
    canvas.renderAll();
    pushHistory();
    syncLayersFromCanvas();
  };

  const handleDeleteCol = () => {
    if (!selectedTable || !canvas || selectedTable._tableCols <= 1) return;
    const colIdx = singleCell ? singleCell.col : selectedTable._tableCols - 1;
    deleteTableColumn(selectedTable, colIdx);
    clearTableCellSelection();
    canvas.renderAll();
    pushHistory();
    syncLayersFromCanvas();
  };

  const handleCellBgColorChange = (color) => {
    if (!selectedTable || !canvas) return;
    if (selectedTableCells.length > 0) {
      selectedTable.getObjects().forEach(child => {
        if (!child._isTableCell) return;
        const match = selectedTableCells.some(c => c.row === child._cellRow && c.col === child._cellCol);
        if (match) child.set({ fill: color });
      });
    }
    canvas.renderAll();
    pushHistory();
  };

  const handleMergeCells = () => {
    if (!selectedTable || selectedTableCells.length < 2 || !canvas) return;
    const success = mergeCells(selectedTable, selectedTableCells, canvas);
    if (success) {
      clearTableCellSelection();
      pushHistory();
    }
  };

  const handleUnmergeCells = () => {
    if (!selectedTable || selectedTableCells.length !== 1 || !canvas) return;
    const { row, col } = selectedTableCells[0];
    const success = unmergeCells(selectedTable, row, col, canvas);
    if (success) {
      clearTableCellSelection();
      pushHistory();
    }
  };

  const handleAutoFitHeight = () => {
    if (!selectedTable || !canvas) return;

    let targetRows;
    if (selectedTableCells.length > 0) {
      targetRows = [...new Set(selectedTableCells.map(c => c.row))];
    } else {
      targetRows = Array.from({ length: selectedTable._tableRows }, (_, i) => i);
    }

    const targetSet = new Set(targetRows);
    const rects = selectedTable.getObjects().filter(c => c._isTableCell);
    for (const rect of rects) {
      if (targetSet.has(rect._cellRow)) {
        rect._marT = DEFAULT_TEXT_INSET.T;
        rect._marB = DEFAULT_TEXT_INSET.B;
      }
    }

    const savedLeft = selectedTable.left;
    const savedTop = selectedTable.top;
    const oldH = selectedTable._tableRowHeights.reduce((a, b) => a + b, 0);

    for (const r of targetRows) {
      selectedTable._tableRowHeights[r] = 0;
    }

    autoFitRowHeights(selectedTable, targetRows);
    const newH = selectedTable._tableRowHeights.reduce((a, b) => a + b, 0);
    const scaleY = selectedTable.scaleY || 1;
    selectedTable.set({ left: savedLeft, top: savedTop + (newH - oldH) * scaleY / 2 });
    selectedTable.setCoords();
    canvas.renderAll();
    pushHistory();
  };

  // --- 테두리 방향 선택 UI ---
  const renderBorderDirSelector = () => {
    const allCellDirs = ['T', 'B', 'L', 'R'];
    const cellAllSelected = allCellDirs.every(d => borderDirs.has(d));

    if (isCellMode && !isMultiCell) {
      // 단일셀 모드: 전체, 왼쪽, 위, 오른쪽, 아래
      const buttons = [
        { key: 'all', label: '전체', icon: <CellBorderIcon dirs={['T','B','L','R']} active={cellAllSelected} /> },
        { key: 'L', label: '왼쪽', icon: <CellBorderIcon dirs={['L']} active={borderDirs.has('L')} /> },
        { key: 'T', label: '위', icon: <CellBorderIcon dirs={['T']} active={borderDirs.has('T')} /> },
        { key: 'R', label: '오른쪽', icon: <CellBorderIcon dirs={['R']} active={borderDirs.has('R')} /> },
        { key: 'B', label: '아래', icon: <CellBorderIcon dirs={['B']} active={borderDirs.has('B')} /> },
      ];
      return (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {buttons.map(btn => (
            <button
              key={btn.key}
              onClick={() => toggleBorderDir(btn.key)}
              title={btn.label}
              style={borderDirBtnStyle(
                btn.key === 'all' ? cellAllSelected : borderDirs.has(btn.key)
              )}
            >
              {btn.icon}
              <span style={{ fontSize: '10px' }}>{btn.label}</span>
            </button>
          ))}
        </div>
      );
    }

    // 다중셀 / 테이블 모드: 전체, 바깥쪽, 안쪽, 왼쪽, 위, 오른쪽, 아래
    const outerSelected = allCellDirs.every(d => borderDirs.has(d));
    const insideSelected = borderDirs.has('inside');
    const allTableSelected = outerSelected && insideSelected;

    // 다중셀이면 TableBorderIcon, 테이블이면 TableBorderIcon (동일한 아이콘)
    const buttons = [
      { key: 'all', label: '전체', icon: <TableBorderIcon outside={['T','B','L','R']} inside active={allTableSelected} /> },
      { key: 'outside', label: '바깥', icon: <TableBorderIcon outside={['T','B','L','R']} active={outerSelected} /> },
      { key: 'inside', label: '안쪽', icon: <TableBorderIcon inside active={insideSelected} /> },
      { key: 'L', label: '왼쪽', icon: <TableBorderIcon outside={['L']} active={borderDirs.has('L')} /> },
      { key: 'T', label: '위', icon: <TableBorderIcon outside={['T']} active={borderDirs.has('T')} /> },
      { key: 'R', label: '오른쪽', icon: <TableBorderIcon outside={['R']} active={borderDirs.has('R')} /> },
      { key: 'B', label: '아래', icon: <TableBorderIcon outside={['B']} active={borderDirs.has('B')} /> },
    ];

    return (
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
        {buttons.map(btn => {
          let isActive;
          if (btn.key === 'all') isActive = allTableSelected;
          else if (btn.key === 'outside') isActive = outerSelected;
          else if (btn.key === 'inside') isActive = insideSelected;
          else isActive = borderDirs.has(btn.key);
          return (
            <button
              key={btn.key}
              onClick={() => toggleBorderDir(btn.key)}
              title={btn.label}
              style={borderDirBtnStyle(isActive)}
            >
              {btn.icon}
              <span style={{ fontSize: '10px' }}>{btn.label}</span>
            </button>
          );
        })}
      </div>
    );
  };

  // --- 그리드 셀렉터 렌더링 ---
  const renderGridSelector = () => {
    const gridWidth = GRID_COLS * (CELL_SIZE + CELL_GAP) - CELL_GAP;

    return (
      <div style={{ ...sectionStyle, marginTop: '20px' }}>
        <label style={{ ...labelStyle, fontSize: '15px' }}>표 크기 선택</label>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${GRID_COLS}, ${CELL_SIZE}px)`,
            gap: `${CELL_GAP}px`,
            width: gridWidth,
            cursor: 'pointer',
            margin: '0 auto',
          }}
          onMouseLeave={() => setHoverCell(null)}
        >
          {Array.from({ length: GRID_ROWS * GRID_COLS }).map((_, idx) => {
            const row = Math.floor(idx / GRID_COLS);
            const col = idx % GRID_COLS;
            const isHighlighted = hoverCell && row <= hoverCell.row && col <= hoverCell.col;
            return (
              <div
                key={idx}
                onMouseEnter={() => setHoverCell({ row, col })}
                onClick={() => handleCreateTable(row + 1, col + 1)}
                style={{
                  width: CELL_SIZE,
                  height: CELL_SIZE,
                  borderRadius: '3px',
                  border: '1px solid #d1d5db',
                  backgroundColor: isHighlighted ? '#3b82f6' : '#f9fafb',
                  opacity: isHighlighted ? 1 : 0.7,
                  transition: 'all 0.05s',
                }}
              />
            );
          })}
        </div>
        <div style={{
          textAlign: 'center', marginTop: '8px', fontSize: '13px',
          color: hoverCell ? '#1f2937' : '#9ca3af', fontWeight: '500',
        }}>
          {hoverCell
            ? `${hoverCell.col + 1} x ${hoverCell.row + 1} 표`
            : '마우스를 올려 크기를 선택하세요'}
        </div>
      </div>
    );
  };

  // --- 수동 입력 렌더링 ---
  const renderManualInput = () => (
    <div style={sectionStyle}>
      <label style={labelStyle}>직접 입력</label>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>열</label>
          <input
            type="number"
            value={manualCols}
            onChange={(e) => setManualCols(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
            min="1" max="20"
            style={{ ...inputStyle, width: '100%' }}
          />
        </div>
        <span style={{ color: '#9ca3af', marginTop: '16px', fontSize: '16px' }}>x</span>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>행</label>
          <input
            type="number"
            value={manualRows}
            onChange={(e) => setManualRows(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
            min="1" max="20"
            style={{ ...inputStyle, width: '100%' }}
          />
        </div>
        <button
          onClick={() => handleCreateTable(manualRows, manualCols)}
          style={{
            marginTop: '16px',
            padding: '8px 16px',
            backgroundColor: '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
          }}
        >
          생성
        </button>
      </div>
    </div>
  );

  // --- 테이블 설정 렌더링 ---
  const renderTableSettings = () => (
    <>
      {/* 테이블 크기 정보 */}
      <div style={{ ...sectionStyle, marginTop: '20px' }}>
        <label style={labelStyle}>테이블 크기</label>
        <div style={{
          display: 'flex', gap: '16px', fontSize: '13px', color: '#374151',
          padding: '8px 12px', backgroundColor: '#f9fafb', borderRadius: '6px',
        }}>
          <span>{selectedTable._tableCols}열 x {selectedTable._tableRows}행</span>
        </div>
      </div>

      {/* 행/열 추가·삭제 */}
      <div style={sectionStyle}>
        <label style={labelStyle}>행</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={handleAddRow} style={actionBtnStyle}>
            <FiPlus size={14} /> 행 추가
          </button>
          <button
            onClick={handleDeleteRow}
            disabled={selectedTable._tableRows <= 1}
            style={actionBtnStyle}
          >
            <FiMinus size={14} /> 행 삭제
          </button>
        </div>
      </div>
      <div style={sectionStyle}>
        <label style={labelStyle}>열</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={handleAddCol} style={actionBtnStyle}>
            <FiPlus size={14} /> 열 추가
          </button>
          <button
            onClick={handleDeleteCol}
            disabled={selectedTable._tableCols <= 1}
            style={actionBtnStyle}
          >
            <FiMinus size={14} /> 열 삭제
          </button>
        </div>
      </div>

      {/* 테두리 방향 선택 */}
      <div style={sectionStyle}>
        <label style={labelStyle}>테두리 방향</label>
        {renderBorderDirSelector()}
      </div>

      {/* 테두리 색상 */}
      <div style={sectionStyle}>
        <ColorPicker
          label="테두리 색상"
          value={currentBorderColor}
          onChange={handleBorderColorChange}
          onOpen={() => setIsBorderColorPickerOpen(true)}
          onClose={() => setIsBorderColorPickerOpen(false)}
          size="large"
        />
      </div>

      {/* 테두리 두께 */}
      <SliderInput
        label="테두리 두께"
        value={currentBorderWidth}
        onChange={handleBorderWidthChange}
        min={0}
        max={5}
        step={0.5}
        unit="px"
      />

      {/* 셀 배경색 (셀 선택 시) */}
      {selectedTableCells.length > 0 && (
        <div style={sectionStyle}>
          <ColorPicker
            label={selectedTableCells.length === 1
              ? `셀 배경색 (${singleCell.row + 1}행 ${singleCell.col + 1}열)`
              : `셀 배경색 (${selectedTableCells.length}개 셀)`}
            value={cellBgColor || 'transparent'}
            onChange={handleCellBgColorChange}
            showTransparent
            size="large"
          />
        </div>
      )}

      {/* 셀 병합/해제 */}
      {(canMerge || canUnmerge) && (
        <div style={sectionStyle}>
          <label style={labelStyle}>셀 병합</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {canMerge && (
              <button onClick={handleMergeCells} style={mergeBtnStyle('#3b82f6')}>
                셀 병합 ({selectedTableCells.length}개)
              </button>
            )}
            {canUnmerge && (
              <button onClick={handleUnmergeCells} style={mergeBtnStyle('#ef4444')}>
                병합 해제
              </button>
            )}
          </div>
        </div>
      )}

      {/* 셀 높이 자동 맞춤 */}
      <div style={sectionStyle}>
        <label style={labelStyle}>셀 높이</label>
        <button onClick={handleAutoFitHeight} style={actionBtnStyle}>
          셀 높이 자동 맞춤
          {selectedTableCells.length > 0
            ? ` (${[...new Set(selectedTableCells.map(c => c.row))].length}개행)`
            : ' (전체)'}
        </button>
      </div>
    </>
  );

  return (
    <div style={panelStyle}>
      <h3 style={titleStyle}>{selectedTable ? '표 설정' : '표 추가'}</h3>

      {!selectedTable && (
        <>
          {renderGridSelector()}
          {renderManualInput()}
        </>
      )}

      {selectedTable && renderTableSettings()}
    </div>
  );
};

// --- 스타일 ---
const actionBtnStyle = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  padding: '8px 12px',
  fontSize: '12px',
  fontWeight: '500',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  backgroundColor: '#ffffff',
  cursor: 'pointer',
  color: '#374151',
};

const mergeBtnStyle = (bgColor) => ({
  flex: 1,
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: '600',
  backgroundColor: bgColor,
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
});

const borderDirBtnStyle = (active) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '2px',
  padding: '4px 6px',
  border: active ? '2px solid #3b82f6' : '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: active ? '#eff6ff' : '#ffffff',
  cursor: 'pointer',
  minWidth: '32px',
  transition: 'all 0.1s',
});

export default TableSettingsPanel;
